import React from "react";
import { Card } from "flowbite-react";
import {
  Clock,
  Sun,
  Sunrise,
  Sunset,
  Moon,
  MountainSnow,
  Sparkles,
} from "lucide-react";
import { useTranslation } from 'react-i18next';

interface PrayerTime {
  name: string;
  time: string;
  icon: React.ReactNode;
  isNext?: boolean;
}

interface PrayerScheduleProps {
  prayerTimes?: PrayerTime[];
  iftarTime?: string;
  currentDate?: string;
  location?: string;
}

const PrayerSchedule = ({
  prayerTimes = [],
  iftarTime,
  currentDate,
  location,
}: PrayerScheduleProps) => {
  const { t } = useTranslation();
  
  return (
    <Card className="w-full max-w-md mx-auto overflow-hidden animate-fadeIn shadow-lg">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-lg">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Clock className="h-5 w-5 text-purple-200" />
          <h2 className="text-xl font-bold">{t('prayerSchedule')}</h2>
        </div>
        <div className="flex flex-col items-center">
          <p className="text-center text-sm opacity-90">
            {currentDate}
          </p>
          <p className="text-center text-xs opacity-80 mt-1">
            {prayerTimes[0]?.time !== "Laddar..." &&
            prayerTimes[0]?.time !== "Fel vid hämtning"
              ? `${t('timesFor')} ${location?.split(",")[0]}`
              : prayerTimes[0]?.time === "Fel vid hämtning"
                ? t('couldNotFetchTimes')
                : t('fetchingTimes')}
          </p>
        </div>
      </div>
      
      <div className="p-4 bg-gradient-to-b from-white to-purple-50 dark:from-gray-800 dark:to-purple-950/30">
        <div className="space-y-3">
          {prayerTimes.map((prayer, index) => (
            <div
              key={prayer.name}
              className={`flex justify-between items-center p-3 rounded-lg transition-all duration-200 ${
                prayer.isNext 
                  ? "bg-gradient-to-r from-purple-100/50 to-indigo-50/50 dark:from-purple-900/30 dark:to-indigo-800/20 border border-purple-200 dark:border-purple-800/30 shadow-sm" 
                  : "hover:bg-white/80 dark:hover:bg-gray-700/30"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-full ${
                    prayer.isNext 
                      ? "bg-gradient-to-br from-purple-600 to-indigo-600 text-white" 
                      : "bg-purple-100 dark:bg-gray-700 text-purple-700 dark:text-white"
                  }`}
                >
                  {prayer.icon}
                </div>
                <span className="font-medium">{prayer.name}</span>
              </div>
              <div className="flex items-center">
                <span
                  className={`${
                    prayer.isNext 
                      ? "font-bold text-purple-700 dark:text-purple-300" 
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  {prayer.time}
                </span>
                {prayer.isNext && (
                  <span className="ml-2 text-xs bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-2 py-0.5 rounded-full">
                    {t('next')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default PrayerSchedule;
